import { Button, DialogActions, InputAdornment, Stack, TextField } from '@mui/material';
import { formatMembershipName } from '@provision/service-level-utils';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { FormSelect } from 'src/components/Forms/FormSelect/form-select';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { CustomSignupLinkIntervalUnit, OfferedProduct } from 'src/graphql';

export interface CustomSignupLinkFormValues {
  partnerProductId: string;
  amountDollars: number;
  intervalUnit: CustomSignupLinkIntervalUnit;
}

enum CustomSignupLinkFieldName {
  PartnerProductId = 'partnerProductId',
  AmountDollars = 'amountDollars',
  IntervalUnit = 'intervalUnit',
}

interface CustomSignupLinkFormProps {
  formMethods: UseFormReturn<CustomSignupLinkFormValues>;
  offeredProducts: OfferedProduct[];
  onCancel: () => void;
  onSubmit: (data: CustomSignupLinkFormValues) => Promise<void>;
  isSubmitting: boolean;
}

interface CustomSignupLinkFieldsProps {
  offeredProducts: OfferedProduct[];
  isSubmitting: boolean;
}

const CustomSignupLinkFields = ({
  offeredProducts,
  isSubmitting,
}: CustomSignupLinkFieldsProps) => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<CustomSignupLinkFormValues>();

  const sortedProducts = useMemo(
    () => offeredProducts.slice().sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [offeredProducts],
  );
  const productOptions = useMemo(
    () =>
      sortedProducts.map((product) => ({
        id: product.partnerProductId,
        label: formatMembershipName(product.displayName),
        value: product.partnerProductId,
      })),
    [sortedProducts],
  );
  const intervalOptions = useMemo(
    () => [
      { id: 'month', label: 'Monthly', value: 'month' },
      { id: 'year', label: 'Annual', value: 'year' },
    ],
    [],
  );

  const selectedProductId = watch(CustomSignupLinkFieldName.PartnerProductId);

  useEffect(() => {
    if (!selectedProductId && sortedProducts[0]?.partnerProductId) {
      setValue(
        CustomSignupLinkFieldName.PartnerProductId,
        sortedProducts[0].partnerProductId,
        {
          shouldDirty: false,
        },
      );
    }
  }, [selectedProductId, setValue, sortedProducts]);

  return (
    <Stack spacing={2.5}>
      <FormSelect
        type="select"
        fullWidth
        size="small"
        variant="outlined"
        name={CustomSignupLinkFieldName.PartnerProductId}
        label="Product"
        options={productOptions}
        required="Please select a product."
        showErrorText
        data-testid="custom-signup-link-product"
        selectProps={{
          disabled: isSubmitting,
        }}
      />

      <Controller
        name={CustomSignupLinkFieldName.AmountDollars}
        control={control}
        rules={{
          required: 'Please enter a valid price.',
          validate: (value) =>
            Number.isFinite(value) && value > 0 ? true : 'Please enter a valid price.',
        }}
        render={({ field }) => (
          <NumericFormat
            customInput={TextField}
            fullWidth
            size="small"
            label="Custom price"
            placeholder="0.00"
            value={field.value || ''}
            disabled={isSubmitting}
            data-testid="custom-signup-link-price"
            thousandSeparator
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            helperText={
              errors.amountDollars?.message || 'Charged in the partner billing currency.'
            }
            error={!!errors.amountDollars}
            onValueChange={(values) => {
              setValue(CustomSignupLinkFieldName.AmountDollars, values.floatValue || 0, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        )}
      />

      <FormSelect
        type="select"
        fullWidth
        size="small"
        variant="outlined"
        name={CustomSignupLinkFieldName.IntervalUnit}
        label="Billing interval"
        options={intervalOptions}
        data-testid="custom-signup-link-interval"
        selectProps={{
          disabled: isSubmitting,
        }}
      />
    </Stack>
  );
};

export const CustomSignupLinkForm = ({
  formMethods,
  offeredProducts,
  onCancel,
  onSubmit,
  isSubmitting,
}: CustomSignupLinkFormProps) => {
  const { handleSubmit } = formMethods;

  return (
    <>
      <GenericForm<CustomSignupLinkFormValues> formMethods={formMethods}>
        <CustomSignupLinkFields
          offeredProducts={offeredProducts}
          isSubmitting={isSubmitting}
        />
      </GenericForm>
      <DialogActions sx={{ mt: 3, px: 0, pb: 0 }}>
        <Button data-testid="custom-signup-link-cancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSubmitting}
          data-testid="custom-signup-link-submit"
          onClick={handleSubmit(onSubmit)}
        >
          {isSubmitting ? 'Generating...' : 'Generate link'}
        </Button>
      </DialogActions>
    </>
  );
};
